// Models
import { RecurrenceSchedule } from "./RecurrenceSchedule";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
import { getDiff, getDiffTemporalAndArrayCustomizer } from "@ms/uno-utilities/lib/local/ObjectUtilities";
/**
 * The recurrence definition entity implementation.
 */ export class RecurrenceOccurrence {
    static fromResource(resource) {
        return new RecurrenceOccurrence({
            seriesId: resource.seriesId,
            occurrenceId: resource.occurrenceId,
            previousInSeriesTaskId: resource.previousInSeriesTaskId,
            nextInSeriesTaskId: resource.nextInSeriesTaskId,
            recurrenceStartDateTime: Temporal.Instant.from(resource.recurrenceStartDateTime),
            schedule: resource.schedule != null ? RecurrenceSchedule.fromResource(resource.schedule) : null
        });
    }
    getClone() {
        return new RecurrenceOccurrence({
            seriesId: this.seriesId,
            occurrenceId: this.occurrenceId,
            previousInSeriesTaskId: this.previousInSeriesTaskId,
            nextInSeriesTaskId: this.nextInSeriesTaskId,
            recurrenceStartDateTime: Temporal.Instant.from(this.recurrenceStartDateTime),
            schedule: this.schedule?.getClone() ?? null
        });
    }
    forSerialization() {
        return {
            seriesId: this.seriesId,
            occurrenceId: this.occurrenceId,
            previousInSeriesTaskId: this.previousInSeriesTaskId,
            nextInSeriesTaskId: this.nextInSeriesTaskId,
            recurrenceStartDateTime: this.recurrenceStartDateTime.toString(),
            schedule: this.schedule?.forSerialization() ?? null
        };
    }
    forCreate() {
        if (this.schedule == null) {
            // Creating a recurrence occurrence with no schedule is not valid.
            throw new Error("RecurrenceOccurrence: Cannot create a recurrence occurrence with no schedule.");
        }
        return {
            schedule: this.schedule.forCreate()
        };
    }
    forUpdate(source) {
        if (source == null) {
            // This is a Creation scenario.
            // No source to compare to, so everything is new.
            return this.forCreate();
        }
        // Use getDiff to get the differences between current and source entity
        const diff = getDiff(source, this, getDiffTemporalAndArrayCustomizer);
        // If diff does not contain schedule, return undefined.
        // Schedule is the only updatable property.
        if (diff.schedule === undefined) {
            return undefined;
        }
        // Schedule is the only updatable property and must be there if the entity is updated.
        // There is a change in the schedule. There are only 3 cases (left, Created is handled above):
        // Terminated, Recreated, Updated.
        // 1. this.schedule is null - Schedule is Terminated. Same as diff.schedule === null. This is the base case.
        // 2. source.schedule is null - Schedule is Recreated from a terminated state.
        // 3. Both are non-null - Schedule is Updated.
        if (diff.schedule !== null) {
            // Schedule is a complex object. Handle the update.
            if (source.schedule == null) {
                // Schedule is recreated from a terminated state. Return schedule forCreate.
                // Follow the same logic as this.forCreate().
                return {
                    schedule: this.schedule.forCreate()
                };
            } else {
                // Schedule is updated, get the update object for the schedule.
                const scheduleUpdate = this.schedule.forUpdate(source.schedule);
                if (scheduleUpdate == null) {
                    // No changes in schedule. This should not happen as we have already detected a change in schedule.
                    throw new Error("RecurrenceOccurrence: Inconsistent state detected in schedule update.");
                }
                return {
                    schedule: scheduleUpdate
                };
            }
        } else {
            // Schedule is terminated.
            return {
                schedule: null
            };
        }
    }
    setRecurrenceSchedule(schedule) {
        return !this.isReadonly() ? new RecurrenceOccurrence({
            ...this.getClone(),
            schedule
        }) : this;
    }
    isActive() {
        return this.schedule != null && !this.isReadonly();
    }
    isReadonly() {
        return this.nextInSeriesTaskId != null;
    }
    generateHash() {
        const result = [
            this.seriesId
        ];
        if (this.occurrenceId != null) {
            result.push(this.occurrenceId.toString());
        }
        if (this.previousInSeriesTaskId != null) {
            result.push(this.previousInSeriesTaskId);
        }
        if (this.nextInSeriesTaskId != null) {
            result.push(this.nextInSeriesTaskId);
        }
        if (this.recurrenceStartDateTime != null) {
            result.push(this.recurrenceStartDateTime.toString());
        }
        if (this.schedule != null) {
            result.push(this.schedule.generateHash());
        }
        return result.join(",");
    }
    constructor(propertyBag){
        this.seriesId = propertyBag.seriesId;
        this.occurrenceId = propertyBag.occurrenceId;
        this.previousInSeriesTaskId = propertyBag.previousInSeriesTaskId;
        this.nextInSeriesTaskId = propertyBag.nextInSeriesTaskId;
        this.recurrenceStartDateTime = propertyBag.recurrenceStartDateTime;
        this.schedule = propertyBag.schedule;
    }
}
